<template>
    <div>
        <DeleteItemModal ref="deleteModal"></DeleteItemModal>
        <RestoreItemModal ref="restoreModal"></RestoreItemModal>
        <AggridList ref="aggridlist"
                    :componentGridOptions="gridOptions"
                    :excelColumns='["id", "name", "short_name", "locality.region.name", "locality.name", "address", "is_loading", "is_unloading", "company.name"]'
                    :service="service"
                    :deleted_filter="can('deleted-address-read')"
        >
            <template v-slot:buttons>
                <router-link
                    v-if="can('create-address')"
                    :to="{name: 'address-create'}" class="btn btn-sm btn-primary"><i
                    class="fas fa-plus-square"></i>
                    <span class="d-none d-sm-inline ml-1">
                        {{ trans("Create") }}
                    </span>
                </router-link>
            </template>
        </AggridList>
    </div>
</template>

<script>

import AddressService from "../../services/AddressService";
import FloatingYesNoFilter from "../../aggrid/filters/FloatingYesNoFilter";
import IdCellRenderer from "../../aggrid/cells/IdCellRenderer";
import IdCellParams from "../../aggrid/cells/IdCellParams";
import CRUDCellRenderer from "../../aggrid/cells/CRUDCellRenderer";
import CRUDCellParams from "../../aggrid/cells/CRUDCellParams";

export default {
    data() {
        return {
            service: AddressService,
            gridOptions: {
                columnDefs: [
                    {
                        ...IdCellParams, cellRendererParams: {
                            getRoute(id) {
                                return {
                                    name: "address-detail",
                                    params: {id}
                                };
                            }
                        }
                    },
                    {
                        headerName: this.trans("address.Short name"), field: "short_name", width: 400,
                        sort: "asc",
                        floatingFilter: true,
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            if (prop.data.deleted_at) {
                                return `<del>${prop.value}</del>`;
                            }
                            return prop.value;
                        }
                    },
                    {
                        headerName: this.trans("Region"), field: "locality.region.name", width: 400,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: {
                            suppressFilterButton: true,
                        },
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            if (prop.data.locality.region.deleted_at) {
                                return `<del>${prop.value}</del>`;
                            }
                            return prop.value;
                        }
                    },
                    {
                        headerName: this.trans("address.Locality"), field: "locality.name", width: 400,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: {
                            suppressFilterButton: true,
                        },
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            if (prop.data.locality.deleted_at) {
                                return `<del>${prop.value}</del>`;
                            }
                            return prop.value;
                        }
                    },
                    {
                        headerName: this.trans("address.Address"), field: "address", width: 400,
                        floatingFilter: true,
                    },
                    {
                        headerName: this.trans("address.Is loading"), field: "is_loading", width: 120,
                        filter: "agNumberColumnFilter",
                        suppressMenu: true,
                        floatingFilter: true,
                        floatingFilterComponent: "FloatingYesNoFilter",
                        floatingFilterComponentParams: {
                            suppressFilterButton: true
                        },
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            return prop.value ? '<i class="fas fa-check"></i>' : null;
                        }
                    },
                    {
                        headerName: this.trans("address.Is unloading"), field: "is_unloading", width: 120,
                        filter: "agNumberColumnFilter",
                        suppressMenu: true,
                        floatingFilter: true,
                        floatingFilterComponent: "FloatingYesNoFilter",
                        floatingFilterComponentParams: {
                            suppressFilterButton: true
                        },
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            return prop.value ? '<i class="fas fa-check"></i>' : null;
                        }
                    },
                    {
                        headerName: this.trans("Company"), field: "company.name", width: 220,
                        floatingFilter: true,
                        suppressMenu: true,
                        floatingFilterComponentParams: {
                            suppressFilterButton: true,
                        },
                        cellRenderer: (prop) => {
                            if (!prop.value) return null;
                            if (prop.data.company.deleted_at) {
                                return `<del>${prop.value}</del>`;
                            }
                            return prop.value;
                        }
                    },
                    {
                        ...CRUDCellParams,
                        cellRendererParams: {
                            canRead: (params) => {
                                return this.can("read-address", params.data);
                            },
                            readCb: (id) => {
                                this.$router.push({name: "address-details", params: {id}});
                            },
                            canEdit: (params) => {
                                return this.can("update-address", params.data);
                            },
                            editCb: (id) => {
                                this.$router.push({name: "address-edit", params: {id}});
                            },
                            canDelete: (params) => {
                                return this.can("delete-address", params.data);
                            },
                            canRestore: (params) => {
                                return this.can("restore-address", params.data);
                            },
                            deleteCb: (id) => {
                                this.$refs.deleteModal.show(async () => {
                                    try {
                                        await AddressService.remove(id);
                                        this.$refs.aggridlist.reload();
                                        this.swalSuccess(this.trans("address.Address deleted"));
                                    } catch (error) {
                                        let errorMsg = this.getHttpError(error, this.trans("Delete Error"));
                                        this.swalError(errorMsg);
                                    }
                                });
                            },
                            restoreCb: (id) => {
                                this.$refs.restoreModal.show(async () => {
                                    try {
                                        await AddressService.restore(id);
                                        this.$refs.aggridlist.reload();
                                        this.swalSuccess(this.trans("address.Address restored"));
                                    } catch (error) {
                                        let errorMsg = this.getHttpError(error, this.trans("Restore Error"));
                                        this.swalError(errorMsg);
                                    }
                                });
                            }
                        }
                    }
                ],
                frameworkComponents: {
                    FloatingYesNoFilter, IdCellRenderer, CRUDCellRenderer
                }
            }
        }
    }
}
</script>
